import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SeedsIcon from '../../../assets/seed-icon.svg';

interface SeedRewardBadgeProps {
  seedReward: number;
}

const SeedRewardBadge: React.FC<SeedRewardBadgeProps> = ({ seedReward }) => {
  const [showPopover, setShowPopover] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    setShowPopover(!showPopover);
  };

  const handleMouseEnter = () => {
    setShowPopover(true);
  };

  const handleMouseLeave = () => {
    setShowPopover(false);
  };

  return (
    <div className="relative inline-block">
      <motion.div
        className="relative flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2.5 py-0.5 sm:py-1 bg-growth-green-50 rounded-full border border-growth-green-200 cursor-help"
        animate={{
          scale: [1, 1.05, 1]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut'
        }}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Smooth breathing glow effect */}
        <motion.div
          className="absolute -inset-0.5 sm:-inset-1 rounded-full bg-growth-green-400/15 sm:bg-growth-green-400/25 blur-sm -z-10"
          animate={{
            opacity: [0, 0.6, 0],
            scale: [0.85, 1.15, 0.85]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
        {/* Particle effects around the seed reward */}
        <motion.div
          className="absolute inset-0 rounded-full hidden sm:block"
          animate={{
            opacity: [0.2, 0.5, 0.2],
            scale: [0.95, 1.05, 0.95]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        >
          <motion.div
            className="absolute -top-1 -right-2 w-1.5 h-1.5 bg-growth-green-400 rounded-full"
            animate={{
              x: [0, 3, -2, 2, 0],
              y: [0, -3, 1, -2, 0],
              opacity: [0, 0.3, 0.9, 0.4, 0],
              scale: [0.6, 1.0, 1.4, 0.8, 0.6]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
              times: [0, 0.2, 0.4, 0.7, 1]
            }}
          />
          <motion.div
            className="absolute -bottom-1 -left-2 w-1 h-1 bg-growth-green-500 rounded-full"
            animate={{
              x: [0, -2, 3, -1, 0],
              y: [0, 2, -3, 1, 0],
              opacity: [0, 0.4, 0.8, 0.9, 0.2, 0],
              scale: [0.5, 0.9, 1.3, 1.1, 0.7, 0.5]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.15,
              times: [0, 0.15, 0.35, 0.55, 0.8, 1]
            }}
          />
          <motion.div
            className="absolute top-1 left-1 w-0.5 h-0.5 bg-growth-green-300 rounded-full"
            animate={{
              x: [0, 2, -2, 3, -1, 0],
              y: [0, -2, 2, -3, 1, 0],
              opacity: [0, 0.2, 0.6, 0.9, 0.5, 0],
              scale: [0.4, 0.8, 1.2, 1.4, 0.9, 0.4]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.3,
              times: [0, 0.15, 0.3, 0.5, 0.75, 1]
            }}
          />
          <motion.div
            className="absolute -top-2 -left-1 w-0.5 h-0.5 bg-growth-green-400 rounded-full"
            animate={{
              x: [0, -1, 2, -3, 1, 0],
              y: [0, -2, -1, 3, -2, 0],
              opacity: [0, 0.3, 0.7, 0.8, 0.9, 0.1, 0],
              scale: [0.3, 0.7, 1.1, 1.5, 1.0, 0.6, 0.3]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.45,
              times: [0, 0.1, 0.25, 0.4, 0.6, 0.85, 1]
            }}
          />
        </motion.div>
        <img src={SeedsIcon} alt="Seeds" className="w-3 h-3 sm:w-4 sm:h-4 relative z-10" />
        <span className="text-[10px] sm:text-xs font-bold text-growth-green-800 relative z-10 whitespace-nowrap">
          Earn {seedReward} {seedReward === 1 ? 'Seed' : 'Seeds'}
        </span>
      </motion.div>

      {/* Popover */}
      <AnimatePresence mode="wait">
        {showPopover && (
          <motion.div
            key="seed-popover"
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="absolute bottom-full right-0 mb-1.5 sm:mb-2 w-56 sm:w-64 pointer-events-none"
            style={{ zIndex: 1000 }}
          >
            <div
              className="rounded-lg shadow-2xl border border-green-200 p-2.5 sm:p-3 relative"
              style={{
                backgroundColor: '#ffffff'
              }}
            >
              {/* Arrow pointing down */}
              <div
                className="absolute -bottom-1.5 right-5 sm:right-6 w-3 h-3 border-r border-b border-green-200 transform rotate-45"
                style={{
                  backgroundColor: '#ffffff'
                }}
              ></div>

              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <img src={SeedsIcon} alt="Seeds" className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <div className="flex-1">
                  <h4
                    className="text-xs sm:text-sm font-bold mb-0.5 sm:mb-1"
                    style={{
                      color: '#111827',
                      fontWeight: 700,
                      lineHeight: 1.3
                    }}
                  >
                    Earn Seeds 🌱
                  </h4>
                  <p
                    className="text-[10px] sm:text-xs leading-relaxed"
                    style={{
                      color: '#374151',
                      lineHeight: 1.5
                    }}
                  >
                    Complete this module and pass the quiz to earn{' '}
                    <span
                      className="font-bold"
                      style={{
                        color: '#15803d',
                        fontWeight: 700
                      }}
                    >
                      {seedReward} {seedReward === 1 ? 'seed' : 'seeds'}
                    </span>
                    ! Use seeds to invest in articles and earn a portion of their ad revenue.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SeedRewardBadge;
